import { httpsCallable } from 'firebase/functions';
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { app, functions } from '../firebase';
import { notificationService } from './notificationService';

interface RegisterPushTokenRequest {
  token: string;
  platform: string;
  userAgent: string;
}

interface RegisterPushTokenResponse {
  success: boolean;
}

interface UnregisterPushTokenRequest {
  token: string;
}

interface UnregisterPushTokenResponse {
  success: boolean;
}

interface CheckPushTokenRegistrationRequest {
  token: string;
}

interface CheckPushTokenRegistrationResponse {
  registered: boolean;
}

export type PushInitResult =
  | 'enabled'
  | 'already-enabled'
  | 'unsupported'
  | 'permission-default'
  | 'permission-denied'
  | 'missing-vapid'
  | 'brave-push-service-disabled'
  | 'service-worker-error'
  | 'subscription-error'
  | 'token-error'
  | 'register-error'
  | 'activation-unconfirmed'
  | 'no-token';

export type PushActivationState = 'active' | 'inactive' | 'unsupported';

type PushInitStage =
  | 'support'
  | 'permission'
  | 'service-worker'
  | 'subscription'
  | 'token'
  | 'register'
  | 'readback'
  | 'done';

export interface PushInitDiagnostics {
  uid: string;
  requestedPermission: boolean;
  permission: NotificationPermission | 'unsupported';
  supported: boolean | null;
  serviceWorkerRegistered: boolean | null;
  serviceWorkerScope: string | null;
  serviceWorkerScriptUrl: string | null;
  serviceWorkerRegistrationsCount: number | null;
  serviceWorkerRegistrations: ServiceWorkerRegistrationSnapshot[];
  selectedServiceWorkerScope: string | null;
  selectedServiceWorkerScriptUrl: string | null;
  selectedServiceWorkerReason: string | null;
  selectedServiceWorkerIsMessagingScript: boolean | null;
  selectedServiceWorkerOriginMatchesPage: boolean | null;
  selectedServiceWorkerEnvironment: 'prod' | 'non-prod' | 'unknown';
  selectedServiceWorkerEnvironmentReason: string | null;
  readyServiceWorkerScope: string | null;
  readyServiceWorkerScriptUrl: string | null;
  hasConflictingRootServiceWorker: boolean | null;
  serviceWorkerCleanupAttempted: boolean;
  serviceWorkerCleanupSucceeded: boolean | null;
  hasExistingSubscription: boolean | null;
  tokenAcquired: boolean | null;
  tokenRegistered: boolean | null;
  vapidKeyPresent: boolean;
  vapidKeyPreview: string | null;
  firebaseAuthDomain: string | null;
  firebaseStorageBucket: string | null;
  firebaseAppId: string | null;
  firebaseProjectId: string | null;
  firebaseMessagingSenderId: string | null;
  serviceWorkerFirebaseConfig: FirebaseRuntimeConfigSnapshot;
  serviceWorkerFirebaseConfigStatus: ServiceWorkerFirebaseConfigStatus;
  serviceWorkerHasFirebaseInit: boolean | null;
  serviceWorkerHasFirebaseMessagingImport: boolean | null;
  serviceWorkerConfigInspectionError: string | null;
  cleanupAttempted: boolean;
  cleanupSucceeded: boolean | null;
  retryAttempted: boolean;
  retrySucceeded: boolean | null;
  stage: PushInitStage;
  result: PushInitResult | 'exception';
  failingCall?: string;
  errorCode?: string;
  errorMessage?: string;
  timestamp: number;
}

let initializedForUser: string | null = null;
let foregroundUnsubscribe: (() => void) | null = null;
let setupPromise: { uid: string; promise: Promise<PushInitResult> } | null = null;
let registeredToken: string | null = null;
let lastPushInitDiagnostics: PushInitDiagnostics | null = null;
const MESSAGING_SW_PATH = '/firebase-messaging-sw.js';
const BRAND_ASSET_VERSION = '2026-04-18-transparent-v2';
const MOBILE_BRAND_ICON_PATH = `/brand/icons/android-chrome-192x192.png?v=${BRAND_ASSET_VERSION}`;
const EVENT_PUSH_FRESHNESS_MS = 24 * 60 * 60 * 1000;
const REQUEST_PUSH_FRESHNESS_MS = 72 * 60 * 60 * 1000;

interface ServiceWorkerRegistrationSnapshot {
  scope: string;
  activeScriptUrl: string | null;
  waitingScriptUrl: string | null;
  installingScriptUrl: string | null;
  activeState: string | null;
  waitingState: string | null;
  installingState: string | null;
}

interface MessagingServiceWorkerSelection {
  registration: ServiceWorkerRegistration;
  reason: string;
}

interface FirebaseRuntimeConfigSnapshot {
  projectId: string | null;
  messagingSenderId: string | null;
  appId: string | null;
  authDomain: string | null;
  storageBucket: string | null;
}

type ServiceWorkerFirebaseConfigStatus =
  | 'not-inspected'
  | 'missing-in-sw'
  | 'matched'
  | 'mismatched'
  | 'inspect-error';

const registerPushTokenFn = httpsCallable<RegisterPushTokenRequest, RegisterPushTokenResponse>(
  functions,
  'registerPushToken'
);
const unregisterPushTokenFn = httpsCallable<
  UnregisterPushTokenRequest,
  UnregisterPushTokenResponse
>(functions, 'unregisterPushToken');
const checkPushTokenRegistrationFn = httpsCallable<
  CheckPushTokenRegistrationRequest,
  CheckPushTokenRegistrationResponse
>(functions, 'checkPushTokenRegistration');

const canUseBrowserNotifications = (): boolean => {
  return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;
};

const createFirebaseRuntimeConfigSnapshot = (): FirebaseRuntimeConfigSnapshot => {
  return {
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || null,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || null,
    appId: process.env.REACT_APP_FIREBASE_APP_ID || null,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || null,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || null,
  };
};

const createEmptyFirebaseRuntimeConfigSnapshot = (): FirebaseRuntimeConfigSnapshot => ({
  projectId: null,
  messagingSenderId: null,
  appId: null,
  authDomain: null,
  storageBucket: null,
});

const resolveTargetUrl = (payloadData?: Record<string, string | undefined>): string => {
  const rawTarget =
    payloadData?.targetUrl ||
    payloadData?.link ||
    (payloadData?.repairRequestId
      ? `/app/request/${payloadData.repairRequestId}`
      : '/app/my-requests');

  if (!rawTarget) {
    return '/app/my-requests';
  }

  try {
    const resolved = new URL(rawTarget, window.location.origin);
    if (resolved.origin !== window.location.origin) {
      return '/app/my-requests';
    }
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch (_error) {
    return '/app/my-requests';
  }
};

const resolvePushFreshnessLimitMs = (payloadData?: Record<string, string | undefined>): number => {
  const source = String(payloadData?.source || '').trim();
  const eventType = String(payloadData?.eventType || payloadData?.type || '').trim();
  if (
    source === 'repairRequests' ||
    eventType === 'chat_message' ||
    eventType === 'request_status'
  ) {
    return REQUEST_PUSH_FRESHNESS_MS;
  }
  return EVENT_PUSH_FRESHNESS_MS;
};

const parseCreatedAtMs = (value: unknown): number | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return Math.trunc(parsed);
};

export const shouldDisplayBrowserPushPayload = (
  payloadData?: Record<string, string | undefined>,
  nowMs = Date.now()
): { show: boolean; reason: string; createdAtMs: number | null; ageMs: number | null } => {
  const createdAtMs = parseCreatedAtMs(payloadData?.createdAtMs);
  if (!createdAtMs) {
    return { show: false, reason: 'missing-createdAtMs', createdAtMs: null, ageMs: null };
  }

  const ageMs = nowMs - createdAtMs;
  if (ageMs < 0) {
    return { show: true, reason: 'fresh-clock-skew', createdAtMs, ageMs };
  }

  const freshnessLimitMs = resolvePushFreshnessLimitMs(payloadData);
  if (ageMs > freshnessLimitMs) {
    return { show: false, reason: 'stale-payload', createdAtMs, ageMs };
  }

  return { show: true, reason: 'fresh', createdAtMs, ageMs };
};

export const appendNotificationIdToTarget = (
  targetUrl: string,
  notificationId?: string
): string => {
  const normalizedNotificationId = String(notificationId || '').trim();
  if (!normalizedNotificationId || typeof window === 'undefined') {
    return targetUrl;
  }

  try {
    const resolved = new URL(targetUrl, window.location.origin);
    if (resolved.origin !== window.location.origin) {
      return '/app/my-requests';
    }
    if (!resolved.searchParams.has('notificationId')) {
      resolved.searchParams.set('notificationId', normalizedNotificationId);
    }
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch (_error) {
    return '/app/my-requests';
  }
};

const isEventNotificationPayload = (payloadData?: Record<string, string | undefined>): boolean => {
  return Boolean(String(payloadData?.notificationId || '').trim());
};

const isPayloadAlreadyReadOrDismissed = async (
  payloadData?: Record<string, string | undefined>
): Promise<boolean> => {
  const notificationId = String(payloadData?.notificationId || '').trim();
  if (!notificationId) {
    return false;
  }

  try {
    return await notificationService.isNotificationReadOrDismissed(notificationId);
  } catch (error) {
    console.warn('[Push][Foreground] Could not verify notification state.', {
      notificationId,
      error,
    });
    return false;
  }
};

const setupForegroundListener = (): void => {
  if (foregroundUnsubscribe) {
    return;
  }

  const messaging = getMessaging(app);
  foregroundUnsubscribe = onMessage(messaging, async (payload) => {
    const title = payload.notification?.title || 'Nowe powiadomienie';
    const body = payload.notification?.body || '';
    console.log('[Push][Foreground]', title, body, payload.data);
    const payloadData = payload.data as Record<string, string | undefined> | undefined;
    const displayDecision = shouldDisplayBrowserPushPayload(payloadData);
    if (!displayDecision.show) {
      console.info('[Push][Foreground] Suppressed push payload', {
        reason: displayDecision.reason,
        eventType: payloadData?.eventType || payloadData?.type || 'general',
        orderId: payloadData?.orderId || payloadData?.repairRequestId || '',
        notificationId: payloadData?.notificationId || '',
      });
      return;
    }

    if (
      isEventNotificationPayload(payloadData) &&
      (await isPayloadAlreadyReadOrDismissed(payloadData))
    ) {
      console.info('[Push][Foreground] Suppressed read/dismissed notification payload', {
        notificationId: payloadData?.notificationId,
      });
      return;
    }

    // On web, FCM does not always show system notifications in foreground.
    // We display it explicitly when permission is granted.
    if (Notification.permission === 'granted') {
      const link = appendNotificationIdToTarget(
        resolveTargetUrl(payloadData),
        payloadData?.notificationId
      );
      const tag =
        payloadData?.tag ||
        payloadData?.notificationId ||
        payloadData?.orderId ||
        payloadData?.repairRequestId ||
        'general';

      const notification = new Notification(title, {
        body,
        icon: MOBILE_BRAND_ICON_PATH,
        tag,
        renotify: false,
        timestamp: displayDecision.createdAtMs || Date.now(),
        data: {
          targetUrl: link,
          orderId: payloadData?.orderId || payloadData?.repairRequestId || '',
          eventType: payloadData?.eventType || payloadData?.type || 'general',
          notificationId: payloadData?.notificationId || '',
        },
      });

      notification.onclick = () => {
        window.focus();
        window.location.assign(link);
      };
    }
  });
};

export const startForegroundPushDelivery = (): void => {
  setupForegroundListener();
};

export const stopForegroundPushDelivery = (): void => {
  foregroundUnsubscribe?.();
  foregroundUnsubscribe = null;
};

const requestPermissionIfNeeded = async (): Promise<NotificationPermission> => {
  if (Notification.permission === 'granted') {
    return Notification.permission;
  }
  if (Notification.permission === 'denied') {
    return Notification.permission;
  }
  return Notification.requestPermission();
};

const getErrorSummary = (error: unknown): { code: string; message: string; name: string } => {
  const anyError = error as { code?: unknown; message?: unknown; name?: unknown };
  return {
    code: String(anyError?.code || ''),
    message: String(anyError?.message || error || ''),
    name: String(anyError?.name || ''),
  };
};

const isBraveBrowser = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const braveNavigator = navigator as Navigator & { brave?: unknown };
  return Boolean(braveNavigator.brave);
};

const toComparableScriptPath = (scriptUrl: string | null | undefined): string | null => {
  if (!scriptUrl) {
    return null;
  }
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://local.invalid';
    const resolved = new URL(scriptUrl, base);
    return resolved.pathname || null;
  } catch (_error) {
    return null;
  }
};

const getRegistrationScriptUrl = (
  registration: ServiceWorkerRegistration | null | undefined
): string | null => {
  if (!registration) {
    return null;
  }
  return (
    registration.active?.scriptURL ||
    registration.waiting?.scriptURL ||
    registration.installing?.scriptURL ||
    null
  );
};

const isMessagingServiceWorkerScript = (scriptUrl: string | null | undefined): boolean => {
  return toComparableScriptPath(scriptUrl) === MESSAGING_SW_PATH;
};

const normalizeScopeUrl = (scope: string | null | undefined): string | null => {
  if (!scope) {
    return null;
  }
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://local.invalid';
    return new URL(scope, base).href;
  } catch (_error) {
    return scope;
  }
};

const isSameOriginScriptUrl = (scriptUrl: string | null | undefined): boolean => {
  if (!scriptUrl) {
    return false;
  }
  try {
    return new URL(scriptUrl, window.location.origin).origin === window.location.origin;
  } catch (_error) {
    return false;
  }
};

const classifyServiceWorkerEnvironment = (
  registration: ServiceWorkerRegistration
): { environment: 'prod' | 'non-prod' | 'unknown'; reason: string } => {
  const scriptUrl = getRegistrationScriptUrl(registration);
  if (!scriptUrl) {
    return { environment: 'unknown', reason: 'missing-script-url' };
  }

  const sameOrigin = isSameOriginScriptUrl(scriptUrl);
  const messagingScript = isMessagingServiceWorkerScript(scriptUrl);
  const expectedRootScope = new URL('/', window.location.origin).href;
  const rootScope = normalizeScopeUrl(registration.scope) === expectedRootScope;

  if (sameOrigin && messagingScript && rootScope) {
    return { environment: 'prod', reason: 'same-origin-root-messaging-script' };
  }
  if (!sameOrigin) {
    return { environment: 'non-prod', reason: 'cross-origin-script-url' };
  }
  if (!messagingScript) {
    return { environment: 'non-prod', reason: 'script-path-not-firebase-messaging-sw' };
  }
  if (!rootScope) {
    return { environment: 'non-prod', reason: 'non-root-scope' };
  }
  return { environment: 'unknown', reason: 'unclassified' };
};

const buildServiceWorkerSnapshot = (
  registration: ServiceWorkerRegistration
): ServiceWorkerRegistrationSnapshot => {
  return {
    scope: registration.scope,
    activeScriptUrl: registration.active?.scriptURL || null,
    waitingScriptUrl: registration.waiting?.scriptURL || null,
    installingScriptUrl: registration.installing?.scriptURL || null,
    activeState: registration.active?.state || null,
    waitingState: registration.waiting?.state || null,
    installingState: registration.installing?.state || null,
  };
};

const collectServiceWorkerSnapshots = (
  registrations: readonly ServiceWorkerRegistration[]
): ServiceWorkerRegistrationSnapshot[] => {
  return registrations.map((registration) => buildServiceWorkerSnapshot(registration));
};

const dedupeRegistrations = (
  ...groups: ReadonlyArray<readonly ServiceWorkerRegistration[]>
): ServiceWorkerRegistration[] => {
  const seen = new Set<ServiceWorkerRegistration>();
  const result: ServiceWorkerRegistration[] = [];
  groups.forEach((group) => {
    group.forEach((registration) => {
      if (!seen.has(registration)) {
        seen.add(registration);
        result.push(registration);
      }
    });
  });
  return result;
};

const selectMessagingServiceWorkerRegistration = (
  registrations: readonly ServiceWorkerRegistration[],
  readyRegistration: ServiceWorkerRegistration | null,
  requestedRegistration: ServiceWorkerRegistration | null,
  expectedRootScope: string
): MessagingServiceWorkerSelection | null => {
  const candidates = dedupeRegistrations(
    registrations,
    readyRegistration ? [readyRegistration] : [],
    requestedRegistration ? [requestedRegistration] : []
  );

  const prioritizedReasons = [
    {
      reason: 'root-active-messaging-script',
      match: (registration: ServiceWorkerRegistration) =>
        normalizeScopeUrl(registration.scope) === expectedRootScope &&
        isMessagingServiceWorkerScript(registration.active?.scriptURL),
    },
    {
      reason: 'ready-root-messaging-script',
      match: (registration: ServiceWorkerRegistration) =>
        Boolean(readyRegistration) &&
        registration === readyRegistration &&
        normalizeScopeUrl(registration.scope) === expectedRootScope &&
        isMessagingServiceWorkerScript(getRegistrationScriptUrl(registration)),
    },
    {
      reason: 'requested-root-messaging-script',
      match: (registration: ServiceWorkerRegistration) =>
        Boolean(requestedRegistration) &&
        registration === requestedRegistration &&
        normalizeScopeUrl(registration.scope) === expectedRootScope &&
        isMessagingServiceWorkerScript(getRegistrationScriptUrl(registration)),
    },
    {
      reason: 'root-messaging-script-fallback',
      match: (registration: ServiceWorkerRegistration) =>
        normalizeScopeUrl(registration.scope) === expectedRootScope &&
        isMessagingServiceWorkerScript(getRegistrationScriptUrl(registration)),
    },
    {
      reason: 'ready-messaging-script-fallback',
      match: (registration: ServiceWorkerRegistration) =>
        Boolean(readyRegistration) &&
        registration === readyRegistration &&
        isMessagingServiceWorkerScript(getRegistrationScriptUrl(registration)),
    },
    {
      reason: 'requested-messaging-script-fallback',
      match: (registration: ServiceWorkerRegistration) =>
        Boolean(requestedRegistration) &&
        registration === requestedRegistration &&
        isMessagingServiceWorkerScript(getRegistrationScriptUrl(registration)),
    },
  ] as const;

  for (const entry of prioritizedReasons) {
    const selected = candidates.find((candidate) => entry.match(candidate));
    if (selected) {
      return {
        registration: selected,
        reason: entry.reason,
      };
    }
  }

  return null;
};

const waitForServiceWorkerActivation = async (
  registration: ServiceWorkerRegistration,
  timeoutMs = 5000
): Promise<void> => {
  if (registration.active) {
    return;
  }
  const pendingWorker = registration.waiting || registration.installing;
  if (!pendingWorker) {
    return;
  }
  if (pendingWorker.state === 'activated') {
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      pendingWorker.removeEventListener('statechange', handleStateChange);
      reject(new Error('service-worker-activation-timeout'));
    }, timeoutMs);
    const handleStateChange = () => {
      if (pendingWorker.state === 'activated' || registration.active) {
        window.clearTimeout(timeout);
        pendingWorker.removeEventListener('statechange', handleStateChange);
        resolve();
      }
    };
    pendingWorker.addEventListener('statechange', handleStateChange);
  });
};

const describeVapidKey = (
  vapidKey: string | undefined
): { present: boolean; preview: string | null } => {
  if (!vapidKey) {
    return { present: false, preview: null };
  }
  return {
    present: true,
    preview: `${vapidKey.slice(0, 8)}...len:${vapidKey.length}`,
  };
};

const isRecoverableTokenAbortError = (error: unknown): boolean => {
  const summary = getErrorSummary(error);
  const normalized = `${summary.code} ${summary.message} ${summary.name}`.toLowerCase();
  return (
    normalized.includes('aborterror') ||
    normalized.includes('push service error') ||
    normalized.includes('token-subscribe-failed')
  );
};

const isBravePushServiceDisabledError = (error: unknown): boolean => {
  if (!isBraveBrowser()) {
    return false;
  }
  const summary = getErrorSummary(error);
  const normalized = `${summary.code} ${summary.message} ${summary.name}`.toLowerCase();
  return normalized.includes('aborterror') && normalized.includes('push service error');
};

const readStringMatch = (scriptContent: string, patterns: readonly RegExp[]): string | null => {
  for (const pattern of patterns) {
    const match = scriptContent.match(pattern);
    const value = match?.[1]?.trim();
    if (value) {
      return value;
    }
  }
  return null;
};

const extractFirebaseConfigFromScriptContent = (
  scriptContent: string
): FirebaseRuntimeConfigSnapshot => {
  return {
    projectId: readStringMatch(scriptContent, [
      /projectId["']?\s*[:=]\s*["']([^"']+)["']/i,
      /REACT_APP_FIREBASE_PROJECT_ID["']?\s*[:=]\s*["']([^"']+)["']/i,
    ]),
    messagingSenderId: readStringMatch(scriptContent, [
      /messagingSenderId["']?\s*[:=]\s*["']([^"']+)["']/i,
      /REACT_APP_FIREBASE_MESSAGING_SENDER_ID["']?\s*[:=]\s*["']([^"']+)["']/i,
    ]),
    appId: readStringMatch(scriptContent, [
      /appId["']?\s*[:=]\s*["']([^"']+)["']/i,
      /REACT_APP_FIREBASE_APP_ID["']?\s*[:=]\s*["']([^"']+)["']/i,
    ]),
    authDomain: readStringMatch(scriptContent, [
      /authDomain["']?\s*[:=]\s*["']([^"']+)["']/i,
      /REACT_APP_FIREBASE_AUTH_DOMAIN["']?\s*[:=]\s*["']([^"']+)["']/i,
    ]),
    storageBucket: readStringMatch(scriptContent, [
      /storageBucket["']?\s*[:=]\s*["']([^"']+)["']/i,
      /REACT_APP_FIREBASE_STORAGE_BUCKET["']?\s*[:=]\s*["']([^"']+)["']/i,
    ]),
  };
};

const compareFirebaseRuntimeConfigSnapshots = (
  pageConfig: FirebaseRuntimeConfigSnapshot,
  swConfig: FirebaseRuntimeConfigSnapshot
): boolean => {
  return (
    pageConfig.projectId === swConfig.projectId &&
    pageConfig.messagingSenderId === swConfig.messagingSenderId &&
    pageConfig.appId === swConfig.appId &&
    pageConfig.authDomain === swConfig.authDomain &&
    pageConfig.storageBucket === swConfig.storageBucket
  );
};

const inspectServiceWorkerFirebaseConfig = async (
  scriptUrl: string | null,
  pageConfig: FirebaseRuntimeConfigSnapshot
): Promise<{
  serviceWorkerFirebaseConfig: FirebaseRuntimeConfigSnapshot;
  serviceWorkerFirebaseConfigStatus: ServiceWorkerFirebaseConfigStatus;
  serviceWorkerHasFirebaseInit: boolean | null;
  serviceWorkerHasFirebaseMessagingImport: boolean | null;
  serviceWorkerConfigInspectionError: string | null;
}> => {
  if (!scriptUrl || typeof window === 'undefined') {
    return {
      serviceWorkerFirebaseConfig: createEmptyFirebaseRuntimeConfigSnapshot(),
      serviceWorkerFirebaseConfigStatus: 'not-inspected',
      serviceWorkerHasFirebaseInit: null,
      serviceWorkerHasFirebaseMessagingImport: null,
      serviceWorkerConfigInspectionError: null,
    };
  }

  try {
    const response = await fetch(scriptUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) {
      throw new Error(`sw-script-fetch-failed:${response.status}`);
    }

    const scriptContent = await response.text();
    const swConfig = extractFirebaseConfigFromScriptContent(scriptContent);
    const hasFirebaseInit = /firebase\.initializeApp\s*\(|initializeApp\s*\(/i.test(scriptContent);
    const hasFirebaseMessagingImport =
      /firebase-messaging|getMessaging|firebase\.messaging\s*\(/i.test(scriptContent);
    const hasAnyConfigValue = Object.values(swConfig).some((value) => Boolean(value));

    let status: ServiceWorkerFirebaseConfigStatus;
    if (!hasAnyConfigValue) {
      status = 'missing-in-sw';
    } else {
      status = compareFirebaseRuntimeConfigSnapshots(pageConfig, swConfig)
        ? 'matched'
        : 'mismatched';
    }

    return {
      serviceWorkerFirebaseConfig: swConfig,
      serviceWorkerFirebaseConfigStatus: status,
      serviceWorkerHasFirebaseInit: hasFirebaseInit,
      serviceWorkerHasFirebaseMessagingImport: hasFirebaseMessagingImport,
      serviceWorkerConfigInspectionError: null,
    };
  } catch (error) {
    const summary = getErrorSummary(error);
    return {
      serviceWorkerFirebaseConfig: createEmptyFirebaseRuntimeConfigSnapshot(),
      serviceWorkerFirebaseConfigStatus: 'inspect-error',
      serviceWorkerHasFirebaseInit: null,
      serviceWorkerHasFirebaseMessagingImport: null,
      serviceWorkerConfigInspectionError: summary.message || summary.name || 'unknown',
    };
  }
};

const mapFailureStageToResult = (stage: PushInitStage): PushInitResult => {
  switch (stage) {
    case 'service-worker':
      return 'service-worker-error';
    case 'subscription':
      return 'subscription-error';
    case 'register':
      return 'register-error';
    case 'readback':
      return 'activation-unconfirmed';
    case 'token':
    default:
      return 'token-error';
  }
};

const registerServiceWorker = async (): Promise<{
  registration: ServiceWorkerRegistration;
  readyRegistration: ServiceWorkerRegistration;
  registrations: readonly ServiceWorkerRegistration[];
  selectionReason: string;
}> => {
  const expectedRootScope = new URL('/', window.location.origin).href;
  const requestedRegistration = await navigator.serviceWorker.register(MESSAGING_SW_PATH, {
    scope: '/',
  });
  const readyRegistration = await navigator.serviceWorker.ready;
  let registrations = await navigator.serviceWorker.getRegistrations();
  let selection = selectMessagingServiceWorkerRegistration(
    registrations,
    readyRegistration,
    requestedRegistration,
    expectedRootScope
  );

  if (!selection) {
    await requestedRegistration.update();
    registrations = await navigator.serviceWorker.getRegistrations();
    selection = selectMessagingServiceWorkerRegistration(
      registrations,
      readyRegistration,
      requestedRegistration,
      expectedRootScope
    );
  }

  if (!selection) {
    throw new Error('service-worker-selection-failed');
  }

  await waitForServiceWorkerActivation(selection.registration);

  return {
    registration: selection.registration,
    readyRegistration,
    registrations,
    selectionReason: selection.reason,
  };
};

const getFcmToken = async (
  registration: ServiceWorkerRegistration,
  vapidKey: string | undefined
): Promise<string | null> => {
  if (!vapidKey) {
    console.warn('[Push] Brak REACT_APP_FIREBASE_VAPID_KEY, pomijam konfiguracje push.');
    return null;
  }

  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });

  return token || null;
};

const getExistingMessagingServiceWorkerRegistration =
  async (): Promise<ServiceWorkerRegistration | null> => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return null;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    const expectedRootScope = new URL('/', window.location.origin).href;
    return (
      selectMessagingServiceWorkerRegistration(registrations, null, null, expectedRootScope)
        ?.registration || null
    );
  };

const getTokenFromExistingPushSubscription = async (
  registration: ServiceWorkerRegistration
): Promise<string | null> => {
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    return null;
  }

  const existingSubscription = await registration.pushManager.getSubscription();
  if (!existingSubscription) {
    return null;
  }

  return getFcmToken(registration, vapidKey);
};

const createPushCleanupError = (cause: unknown): Error => {
  const error = new Error('push-local-cleanup-failed') as Error & {
    code?: string;
    backendUnregistered?: boolean;
    cause?: unknown;
  };
  error.code = 'push-local-cleanup-failed';
  error.backendUnregistered = true;
  error.cause = cause;
  return error;
};

export const isPushLocalCleanupError = (error: unknown): boolean => {
  return Boolean(
    error &&
      typeof error === 'object' &&
      (error as { code?: string; backendUnregistered?: boolean }).code ===
        'push-local-cleanup-failed' &&
      (error as { backendUnregistered?: boolean }).backendUnregistered === true
  );
};

const cleanupLocalPushInstallation = async (
  registration: ServiceWorkerRegistration | null
): Promise<void> => {
  const cleanupErrors: unknown[] = [];

  try {
    const messaging = getMessaging(app);
    await deleteToken(messaging);
  } catch (error) {
    cleanupErrors.push(error);
  }

  if (registration) {
    try {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    } catch (error) {
      cleanupErrors.push(error);
    }
  }

  if (cleanupErrors.length > 0) {
    throw createPushCleanupError(cleanupErrors[0]);
  }
};

export const enablePushNotificationsForCurrentInstallation = async (
  uid: string
): Promise<PushInitResult> => {
  const requestPermission = getPushPermissionStatus() === 'default';
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  const vapidKeyInfo = describeVapidKey(vapidKey);
  const pageFirebaseConfig = createFirebaseRuntimeConfigSnapshot();
  const diagnostics: PushInitDiagnostics = {
    uid,
    requestedPermission: requestPermission,
    permission: getPushPermissionStatus(),
    supported: null,
    serviceWorkerRegistered: null,
    serviceWorkerScope: null,
    serviceWorkerScriptUrl: null,
    serviceWorkerRegistrationsCount: null,
    serviceWorkerRegistrations: [],
    selectedServiceWorkerScope: null,
    selectedServiceWorkerScriptUrl: null,
    selectedServiceWorkerReason: null,
    selectedServiceWorkerIsMessagingScript: null,
    selectedServiceWorkerOriginMatchesPage: null,
    selectedServiceWorkerEnvironment: 'unknown',
    selectedServiceWorkerEnvironmentReason: null,
    readyServiceWorkerScope: null,
    readyServiceWorkerScriptUrl: null,
    hasConflictingRootServiceWorker: null,
    serviceWorkerCleanupAttempted: false,
    serviceWorkerCleanupSucceeded: null,
    hasExistingSubscription: null,
    tokenAcquired: null,
    tokenRegistered: null,
    vapidKeyPresent: vapidKeyInfo.present,
    vapidKeyPreview: vapidKeyInfo.preview,
    firebaseAuthDomain: pageFirebaseConfig.authDomain,
    firebaseStorageBucket: pageFirebaseConfig.storageBucket,
    firebaseAppId: pageFirebaseConfig.appId,
    firebaseProjectId: pageFirebaseConfig.projectId,
    firebaseMessagingSenderId: pageFirebaseConfig.messagingSenderId,
    serviceWorkerFirebaseConfig: createEmptyFirebaseRuntimeConfigSnapshot(),
    serviceWorkerFirebaseConfigStatus: 'not-inspected',
    serviceWorkerHasFirebaseInit: null,
    serviceWorkerHasFirebaseMessagingImport: null,
    serviceWorkerConfigInspectionError: null,
    cleanupAttempted: false,
    cleanupSucceeded: null,
    retryAttempted: false,
    retrySucceeded: null,
    stage: 'support',
    result: 'unsupported',
    timestamp: Date.now(),
  };

  const finalizeDiagnostics = (
    result: PushInitResult | 'exception',
    stage: PushInitStage,
    extra?: Partial<PushInitDiagnostics>
  ) => {
    lastPushInitDiagnostics = {
      ...diagnostics,
      ...extra,
      stage,
      result,
      permission: getPushPermissionStatus(),
      timestamp: Date.now(),
    };
    console.info('[Push][Diag] init', lastPushInitDiagnostics);
  };

  if (!uid || !canUseBrowserNotifications()) {
    finalizeDiagnostics('unsupported', 'support', {
      supported: false,
      serviceWorkerRegistered: false,
      tokenAcquired: false,
      tokenRegistered: false,
    });
    return 'unsupported';
  }

  if (setupPromise?.uid === uid) {
    return setupPromise.promise;
  }

  const promise = (async (): Promise<PushInitResult> => {
    try {
      const supported = await isSupported();
      diagnostics.supported = supported;
      if (!supported) {
        console.log('[Push] Firebase Messaging nie jest wspierane w tym srodowisku.');
        finalizeDiagnostics('unsupported', 'support', {
          serviceWorkerRegistered: false,
          tokenAcquired: false,
          tokenRegistered: false,
        });
        return 'unsupported';
      }

      if (initializedForUser === uid) {
        try {
          const activationState = await resolvePushActivationForCurrentInstallation(uid);
          if (activationState === 'active') {
            finalizeDiagnostics('already-enabled', 'done', {
              serviceWorkerRegistered: true,
              tokenAcquired: true,
              tokenRegistered: true,
            });
            return 'already-enabled';
          }
        } catch (_readbackError) {
          // This is an explicit user action, so a stale local state may proceed to registration.
        }
      }

      diagnostics.stage = 'permission';
      const permission = await requestPermissionIfNeeded();
      diagnostics.permission = permission;
      if (permission !== 'granted') {
        console.log('[Push] Uzytkownik nie wyrazil zgody na powiadomienia.');
        const result = permission === 'denied' ? 'permission-denied' : 'permission-default';
        finalizeDiagnostics(result, 'permission', {
          tokenAcquired: false,
          tokenRegistered: false,
        });
        return result;
      }

      if (!vapidKey) {
        finalizeDiagnostics('missing-vapid', 'token', {
          tokenAcquired: false,
          tokenRegistered: false,
        });
        return 'missing-vapid';
      }

      diagnostics.stage = 'service-worker';
      let registrations: readonly ServiceWorkerRegistration[] = [];
      const rootScope = new URL('/', window.location.origin).href;
      try {
        registrations = await navigator.serviceWorker.getRegistrations();
        diagnostics.serviceWorkerRegistrationsCount = registrations.length;
        diagnostics.serviceWorkerRegistrations = collectServiceWorkerSnapshots(registrations);
        diagnostics.hasConflictingRootServiceWorker = registrations.some((candidate) => {
          return (
            normalizeScopeUrl(candidate.scope) === rootScope &&
            !isMessagingServiceWorkerScript(getRegistrationScriptUrl(candidate))
          );
        });
      } catch (_registrationsError) {
        diagnostics.serviceWorkerRegistrationsCount = null;
        diagnostics.serviceWorkerRegistrations = [];
        diagnostics.hasConflictingRootServiceWorker = null;
      }

      if (diagnostics.hasConflictingRootServiceWorker) {
        diagnostics.serviceWorkerCleanupAttempted = true;
        try {
          const unregisterTasks = registrations
            .filter((candidate) => {
              return (
                normalizeScopeUrl(candidate.scope) === rootScope &&
                !isMessagingServiceWorkerScript(getRegistrationScriptUrl(candidate))
              );
            })
            .map((candidate) => candidate.unregister());
          await Promise.all(unregisterTasks);
          diagnostics.serviceWorkerCleanupSucceeded = true;
          try {
            registrations = await navigator.serviceWorker.getRegistrations();
            diagnostics.serviceWorkerRegistrationsCount = registrations.length;
            diagnostics.serviceWorkerRegistrations = collectServiceWorkerSnapshots(registrations);
          } catch (_refreshError) {
            // best-effort readback
          }
        } catch (_swCleanupError) {
          diagnostics.serviceWorkerCleanupSucceeded = false;
        }
      }

      let registration: ServiceWorkerRegistration;
      let readyRegistration: ServiceWorkerRegistration | null = null;
      try {
        const swResult = await registerServiceWorker();
        registration = swResult.registration;
        readyRegistration = swResult.readyRegistration;
        diagnostics.serviceWorkerRegistrations = collectServiceWorkerSnapshots(
          swResult.registrations
        );
        diagnostics.serviceWorkerRegistrationsCount = swResult.registrations.length;
        diagnostics.selectedServiceWorkerReason = swResult.selectionReason;
      } catch (error) {
        const summary = getErrorSummary(error);
        finalizeDiagnostics('exception', 'service-worker', {
          failingCall: 'navigator.serviceWorker.register/select',
          errorCode: summary.code,
          errorMessage: summary.message || summary.name,
        });
        return 'service-worker-error';
      }
      diagnostics.serviceWorkerRegistered = Boolean(registration);
      diagnostics.serviceWorkerScope = registration.scope || null;
      diagnostics.serviceWorkerScriptUrl = getRegistrationScriptUrl(registration);
      diagnostics.selectedServiceWorkerScope = registration.scope || null;
      diagnostics.selectedServiceWorkerScriptUrl = getRegistrationScriptUrl(registration);
      diagnostics.selectedServiceWorkerIsMessagingScript = isMessagingServiceWorkerScript(
        diagnostics.selectedServiceWorkerScriptUrl
      );
      diagnostics.selectedServiceWorkerOriginMatchesPage = isSameOriginScriptUrl(
        diagnostics.selectedServiceWorkerScriptUrl
      );
      const environmentClassification = classifyServiceWorkerEnvironment(registration);
      diagnostics.selectedServiceWorkerEnvironment = environmentClassification.environment;
      diagnostics.selectedServiceWorkerEnvironmentReason = environmentClassification.reason;
      diagnostics.readyServiceWorkerScope = readyRegistration?.scope || null;
      diagnostics.readyServiceWorkerScriptUrl = getRegistrationScriptUrl(readyRegistration);
      const serviceWorkerInspection = await inspectServiceWorkerFirebaseConfig(
        diagnostics.selectedServiceWorkerScriptUrl,
        pageFirebaseConfig
      );
      diagnostics.serviceWorkerFirebaseConfig = serviceWorkerInspection.serviceWorkerFirebaseConfig;
      diagnostics.serviceWorkerFirebaseConfigStatus =
        serviceWorkerInspection.serviceWorkerFirebaseConfigStatus;
      diagnostics.serviceWorkerHasFirebaseInit =
        serviceWorkerInspection.serviceWorkerHasFirebaseInit;
      diagnostics.serviceWorkerHasFirebaseMessagingImport =
        serviceWorkerInspection.serviceWorkerHasFirebaseMessagingImport;
      diagnostics.serviceWorkerConfigInspectionError =
        serviceWorkerInspection.serviceWorkerConfigInspectionError;

      diagnostics.stage = 'subscription';
      let existingSubscription: PushSubscription | null = null;
      try {
        existingSubscription = await registration.pushManager.getSubscription();
        diagnostics.hasExistingSubscription = Boolean(existingSubscription);
      } catch (error) {
        const summary = getErrorSummary(error);
        finalizeDiagnostics('exception', 'subscription', {
          failingCall: 'registration.pushManager.getSubscription',
          errorCode: summary.code,
          errorMessage: summary.message || summary.name,
        });
        return 'subscription-error';
      }

      diagnostics.stage = 'token';
      const messaging = getMessaging(app);
      let token: string | null = null;
      try {
        token = await getFcmToken(registration, vapidKey);
      } catch (error) {
        const summary = getErrorSummary(error);
        if (isBravePushServiceDisabledError(error)) {
          finalizeDiagnostics('brave-push-service-disabled', 'token', {
            failingCall: 'getToken(initial:selected-registration)',
            errorCode: summary.code,
            errorMessage: summary.message || summary.name,
          });
          return 'brave-push-service-disabled';
        }
        const recoverable = isRecoverableTokenAbortError(error);

        if (!recoverable) {
          finalizeDiagnostics('exception', 'token', {
            failingCall: 'getToken(initial:selected-registration)',
            errorCode: summary.code,
            errorMessage: summary.message || summary.name,
          });
          return 'token-error';
        }

        diagnostics.cleanupAttempted = true;
        try {
          if (existingSubscription) {
            await existingSubscription.unsubscribe();
          }
          await deleteToken(messaging);
          diagnostics.cleanupSucceeded = true;
        } catch (cleanupError) {
          const cleanupSummary = getErrorSummary(cleanupError);
          diagnostics.cleanupSucceeded = false;
          finalizeDiagnostics('exception', 'token', {
            failingCall: 'cleanup(unsubscribe/deleteToken)',
            errorCode: cleanupSummary.code,
            errorMessage: cleanupSummary.message || cleanupSummary.name,
          });
          return 'token-error';
        }

        diagnostics.retryAttempted = true;
        try {
          const refreshedSubscription = await registration.pushManager.getSubscription();
          diagnostics.hasExistingSubscription = Boolean(refreshedSubscription);
        } catch (_ignoreError) {
          // optional readback only
        }

        try {
          token = await getFcmToken(registration, vapidKey);
          diagnostics.retrySucceeded = Boolean(token);
        } catch (retryError) {
          const retrySummary = getErrorSummary(retryError);
          if (isBravePushServiceDisabledError(retryError)) {
            diagnostics.retrySucceeded = false;
            finalizeDiagnostics('brave-push-service-disabled', 'token', {
              failingCall: 'getToken(retry-after-cleanup:selected-registration)',
              errorCode: retrySummary.code,
              errorMessage: retrySummary.message || retrySummary.name,
            });
            return 'brave-push-service-disabled';
          }
          diagnostics.retrySucceeded = false;
          finalizeDiagnostics('exception', 'token', {
            failingCall: 'getToken(retry-after-cleanup:selected-registration)',
            errorCode: retrySummary.code,
            errorMessage: retrySummary.message || retrySummary.name,
          });
          return 'token-error';
        }
      }

      diagnostics.tokenAcquired = Boolean(token);
      if (!token) {
        finalizeDiagnostics('no-token', 'token', {
          tokenRegistered: false,
        });
        return 'no-token';
      }

      diagnostics.stage = 'register';
      try {
        await registerPushTokenFn({
          token,
          platform: 'web',
          userAgent: navigator.userAgent,
        });
      } catch (error) {
        const summary = getErrorSummary(error);
        finalizeDiagnostics('exception', 'register', {
          failingCall: 'registerPushToken(callable)',
          errorCode: summary.code,
          errorMessage: summary.message || summary.name,
        });
        return 'register-error';
      }
      diagnostics.tokenRegistered = true;

      diagnostics.stage = 'readback';
      try {
        const activationState = await resolvePushActivationForCurrentInstallation(uid);
        if (activationState !== 'active') {
          finalizeDiagnostics('activation-unconfirmed', 'readback');
          return 'activation-unconfirmed';
        }
      } catch (error) {
        const summary = getErrorSummary(error);
        finalizeDiagnostics('activation-unconfirmed', 'readback', {
          failingCall: 'checkPushTokenRegistration(readback)',
          errorCode: summary.code,
          errorMessage: summary.message || summary.name,
        });
        return 'activation-unconfirmed';
      }

      console.info('[Push] Token registration confirmed', { uid });
      console.log('[Push] Powiadomienia push skonfigurowane pomyslnie.');
      finalizeDiagnostics('enabled', 'done');
      return 'enabled';
    } catch (error: any) {
      const summary = getErrorSummary(error);
      const mappedResult = mapFailureStageToResult(diagnostics.stage);
      finalizeDiagnostics('exception', diagnostics.stage, {
        failingCall: 'enablePushNotificationsForCurrentInstallation(unhandled)',
        errorCode: summary.code,
        errorMessage: summary.message || summary.name || 'unknown',
      });
      return mappedResult;
    }
  })();
  setupPromise = { uid, promise };

  try {
    return await promise;
  } finally {
    if (setupPromise?.promise === promise) {
      setupPromise = null;
    }
  }
};

export const getLastPushInitDiagnostics = (): PushInitDiagnostics | null => {
  return lastPushInitDiagnostics;
};

export const getPushPermissionStatus = (): NotificationPermission | 'unsupported' => {
  if (!canUseBrowserNotifications()) {
    return 'unsupported';
  }
  return Notification.permission;
};

export const resolvePushActivationForCurrentInstallation = async (
  uid: string
): Promise<PushActivationState> => {
  if (!uid) {
    return 'inactive';
  }

  const permission = getPushPermissionStatus();
  if (permission === 'unsupported') {
    return 'unsupported';
  }
  if (permission !== 'granted') {
    return 'inactive';
  }

  const supported = await isSupported();
  if (!supported) {
    return 'unsupported';
  }

  const registration = await getExistingMessagingServiceWorkerRegistration();
  if (!registration) {
    return 'inactive';
  }

  const token = await getTokenFromExistingPushSubscription(registration);
  if (!token) {
    return 'inactive';
  }

  const readback = await checkPushTokenRegistrationFn({ token });
  if (readback.data.registered) {
    initializedForUser = uid;
    registeredToken = token;
    return 'active';
  }

  if (initializedForUser === uid && registeredToken === token) {
    initializedForUser = null;
    registeredToken = null;
  }
  return 'inactive';
};

export const __pushInternals = {
  toComparableScriptPath,
  isMessagingServiceWorkerScript,
  normalizeScopeUrl,
  buildServiceWorkerSnapshot,
  selectMessagingServiceWorkerRegistration,
  describeVapidKey,
  isSameOriginScriptUrl,
  classifyServiceWorkerEnvironment,
  extractFirebaseConfigFromScriptContent,
  compareFirebaseRuntimeConfigSnapshots,
  isBravePushServiceDisabledError,
  resetRuntimeStateForTests: () => {
    foregroundUnsubscribe?.();
    foregroundUnsubscribe = null;
    setupPromise = null;
    initializedForUser = null;
    registeredToken = null;
    lastPushInitDiagnostics = null;
  },
};

export const isPushInitializedForUser = (uid: string | null | undefined): boolean => {
  return Boolean(uid && initializedForUser === uid);
};

export const disablePushNotificationsForUser = async (uid: string): Promise<boolean> => {
  if (!uid) {
    return false;
  }

  let tokenToRemove = registeredToken;
  let selectedRegistration: ServiceWorkerRegistration | null = null;

  if (!tokenToRemove) {
    const supported = await isSupported();
    if (!supported || !('serviceWorker' in navigator)) {
      return false;
    }
    selectedRegistration = await getExistingMessagingServiceWorkerRegistration();
    if (!selectedRegistration) {
      return false;
    }
    tokenToRemove = await getTokenFromExistingPushSubscription(selectedRegistration);
  } else {
    selectedRegistration = await getExistingMessagingServiceWorkerRegistration();
  }

  if (!tokenToRemove) {
    return false;
  }

  await unregisterPushTokenFn({ token: tokenToRemove });
  console.info('[Push] Token unregistered', { uid });
  initializedForUser = null;
  registeredToken = null;
  stopForegroundPushDelivery();
  await cleanupLocalPushInstallation(selectedRegistration);
  return true;
};
